import { createSelector } from '@reduxjs/toolkit';
import type { State } from '../types';
import { getOrganization } from './organization/getOrganization';
import { currentEmployeeAsViewModel } from './employees/current-employee-as-view-model';

/**
 * Memoized selector for available tutorials
 * Only recalculates when organization or currentEmployee changes
 *
 * This prevents unnecessary re-renders by ensuring the same array reference
 * is returned when the inputs haven't changed.
 */
export const availableTutorialsSelector = createSelector(
  [(state: State) => getOrganization(state), (state: State) => currentEmployeeAsViewModel(state)],
  (organization, currentEmployee) => {
    const result: Array<{ value: string; text: string }> = [];

    if (!organization || !currentEmployee) {
      return result;
    }

    const id = currentEmployee.uuid;

    // Include all tutorials from tutorials array with active registrations for this user
    // For guides: only the LATEST registration counts (older ones are considered cancelled)
    if (organization.tutorials) {
      organization.tutorials.forEach((tutorial) => {
        if (!tutorial.registrations || tutorial.registrations.length === 0) {
          return;
        }

        let hasActiveParticipant = false;

        if (tutorial.guide) {
          // For guides: only check the LATEST registration (sorted by registrationDate)
          const sortedRegistrations = [...tutorial.registrations].sort(
            (a, b) => new Date(b.registrationDate || 0).getTime() - new Date(a.registrationDate || 0).getTime(),
          );
          const latestRegistration = sortedRegistrations[0];

          if (latestRegistration.participants && latestRegistration.participants.length > 0) {
            const participant = latestRegistration.participants[0];
            hasActiveParticipant = participant.status === 'PENDING' || participant.status === 'IN_PROGRESS';
          }
        } else {
          // For courses: check all registrations for matching employeeUuid
          hasActiveParticipant = tutorial.registrations.some((registration) =>
            registration.participants?.some(
              (participant) =>
                participant.employeeUuid === id &&
                (participant.status === 'PENDING' || participant.status === 'IN_PROGRESS'),
            ),
          );
        }

        if (hasActiveParticipant) {
          result.push({ value: tutorial.id, text: tutorial.title });
        }
      });
    }

    return result;
  },
);
